import React from 'react';
import { Box, Typography, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import ClinicHeaderItem from './ClinicHeaderItem';
import { formatDateHour2 } from '../utils/lib';
import ResourcesList from './ResourcesList';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from '../providers/SnackBarProvider';
import { handleError } from '../utils/lib';
import useNavigationHelperStore from '../store/navigationHelperStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { useTranslation } from 'react-i18next';

function ClinicItem({clinic, onDelete, pet, canEdit, config}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { t } = useTranslation();
  const { setNavigation } = useNavigationHelperStore();
  const [openConfirm, setOpenConfirm] = React.useState(false);

  let tempWeight = '';
  if (clinic.temp)
    tempWeight = ` - ${clinic.temp}${clinic.temp_unit.name}`;

  if (clinic.weight)
    tempWeight += ` - ${clinic.weight}${clinic.weight_unit.name}`;

  const handleEdit = () => {
    setNavigation('/main/view-pet', { state: { id: pet.id, updateLastView: false, activeTab: 1 } });
    navigate('/main/edit-clinic', { state: { updateClinic: clinic, pet: pet } });
  }

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}pets_clinic?id=${clinic.id}`,
        { withCredentials: true }
      );
      if (onDelete) {
        onDelete(clinic.id);
      }
    } catch (error) {
      handleError(error, snackbar);
    }
    setOpenConfirm(false);
  };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const getTitle = () => {
    if (config?.vet?.id === clinic.vet.id) {
      return clinic.user.name;
    }
    return `${clinic.user.name} | ${clinic.vet.name}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        //padding: 2, // Esto reemplaza los padding en LinearLayout
      }}
    >
      <ClinicHeaderItem 
        title={getTitle()}
        subtitle={formatDateHour2(clinic.date, config) + tempWeight}
        age={clinic.age}
        thumbUrl={clinic.user.thumb_url}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canEdit ? handleOpenConfirm : undefined}
      />

      {/* Descripción del componente */}
      <Box
        id="txt_description"
        sx={{
          pt: 2,
          pb: 1,
          pr: 2,
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          rehypePlugins={[rehypeRaw]}
          components={{
            p: ({ children }) => <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-line' }}>{children}</Typography>,
            h1: ({ children }) => <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1, mb: 0.5 }}>{children}</Typography>,
            h2: ({ children }) => <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 1, mb: 0.5 }}>{children}</Typography>,
            h3: ({ children }) => <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 0.5, mb: 0.5 }}>{children}</Typography>,
            li: ({ children }) => <Typography component="li" variant="body2" sx={{ ml: 2 }}>{children}</Typography>,
            ul: ({ children }) => <Box component="ul" sx={{ pl: 2, my: 0.5 }}>{children}</Box>,
            ol: ({ children }) => <Box component="ol" sx={{ pl: 2, my: 0.5 }}>{children}</Box>,
            hr: () => <Divider sx={{ my: 1 }} />,
            code: ({ children, className }) => {
              const isBlock = className?.startsWith('language-');
              if (isBlock) {
                return (
                  <Box component="pre" sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1, my: 1, overflow: 'auto', fontSize: '0.8rem' }}>
                    <code>{children}</code>
                  </Box>
                );
              }
              return <Box component="code" sx={{ bgcolor: 'action.hover', borderRadius: 0.5, px: 0.5, fontSize: '0.85em' }}>{children}</Box>;
            },
            pre: ({ children }) => <>{children}</>,
            a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>{children}</a>,
          }}
        >
          {clinic.description}
        </ReactMarkdown>
      </Box>

      {clinic.resources?.length > 0 && (
        <ResourcesList 
          resources={clinic.resources}
        />
      )}

      {/* Línea divisoria */}
      <Divider
        id="div_line"
        sx={{
          marginTop: 1,
          marginBottom: 1,
          marginLeft: 0,
          marginRight: 0,
          backgroundColor: '#dadada',
          height: 1,
        }}
      />

      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
      >
        <DialogTitle>{t('common.confirmDeletion')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('layouts.confirmDeleteConsult')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>  
  );
}

export default ClinicItem;
